import React, { useState } from 'react';
import { CheckCircleIcon, XIcon, ClockIcon } from './icons/InterfaceIcons';
import { ProgressRing } from './ProgressRing';
import { PerformanceAnalytics } from './PerformanceAnalytics';
import { TestCertificate } from './TestCertificate';

const StatCard = ({ label, value, icon, colorClass, subtitle }) => (
    <div className="bg-brand-surface border border-brand-border rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center">
            <div className={`mr-4 p-3 rounded-full ${colorClass} bg-opacity-10`}>
                {icon}
            </div>
            <div className="flex-1">
                <p className="text-sm text-brand-text-secondary font-medium">{label}</p>
                <p className="text-2xl font-bold text-brand-text-primary">{value}</p>
                {subtitle && <p className="text-xs text-brand-text-secondary mt-1">{subtitle}</p>}
            </div>
        </div>
    </div>
);

const PerformanceChart = ({ results }) => {
    const total = results.correct + results.incorrect + results.unattempted;
    const correctPercentage = (results.correct / total) * 100;
    const incorrectPercentage = (results.incorrect / total) * 100;
    const unattemptedPercentage = (results.unattempted / total) * 100;

    return (
        <div className="bg-brand-surface border border-brand-border rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 text-brand-text-primary">Performance Breakdown</h3>
            <div className="flex items-center justify-center mb-4">
                <div className="relative w-32 h-32">
                    <ProgressRing 
                        progress={results.accuracy} 
                        size={128} 
                        strokeWidth={8}
                        // color="#10b981"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-brand-text-primary ">{results.accuracy}%</div>
                            <div className="text-xs text-brand-text-secondary">Accuracy</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-sm text-brand-text-secondary">Correct</span>
                    </div>
                    <span className="text-sm font-semibold">{results.correct} ({correctPercentage.toFixed(1)}%)</span>
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                        <span className="text-sm text-brand-text-secondary">Incorrect</span>
                    </div>
                    <span className="text-sm font-semibold">{results.incorrect} ({incorrectPercentage.toFixed(1)}%)</span>
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div className="w-3 h-3 bg-gray-400 rounded-full mr-2"></div>
                        <span className="text-sm text-brand-text-secondary">Unattempted</span>
                    </div>
                    <span className="text-sm font-semibold">{results.unattempted} ({unattemptedPercentage.toFixed(1)}%)</span>
                </div>
            </div>
        </div>
    );
};

export const ResultsScreen = ({ results, test, onBack, onViewDetails, user }) => {
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [showCertificate, setShowCertificate] = useState(false);
    const timeTaken = new Date(results.time_taken * 1000).toISOString().substr(11, 8);
    const totalQuestions = results.correct + results.incorrect + results.unattempted;
    const attemptedQuestions = results.correct + results.incorrect;
    const scorePercentage = test.totalMarks > 0 ? Math.round((results.score / test.totalMarks) * 100) : 0;
    
    const getPerformanceLevel = () => {
        if (results.accuracy >= 90) return { level: 'Excellent', color: 'text-green-600', bgColor: 'bg-green-100' };
        if (results.accuracy >= 75) return { level: 'Good', color: 'text-blue-600', bgColor: 'bg-blue-100' };
        if (results.accuracy >= 60) return { level: 'Average', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
        return { level: 'Needs Improvement', color: 'text-red-600', bgColor: 'bg-red-100' };
    };
    
    const performance = getPerformanceLevel();
    
    return (
        <div className="min-h-screen bg-brand-bg p-4">
            <div className="w-full max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8 pt-8">
                    <div className="mb-4">
                        <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${performance.bgColor} ${performance.color}`}>
                            {performance.level}
                        </div>
                    </div>
                    <h1 className="text-4xl font-extrabold text-brand-text-primary mb-2">Test Completed!</h1>
                    <p className="text-lg text-brand-text-secondary">Here's your performance summary for "{test.name}"</p>
                </div>

                {/* Main Results Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    {/* Score Card */}
                    <div className="lg:col-span-1">
                        <div className="bg-brand-surface border border-brand-border rounded-xl p-8 text-center h-full">
                            <h3 className="text-sm font-semibold uppercase text-brand-primary mb-4">Your Score</h3>
                            <div className="mb-4">
                                <p className="text-6xl font-bold text-brand-primary">{results.score}</p>
                                <p className="text-lg text-brand-text-secondary">out of {test.totalMarks}</p>
                            </div>
                            <div className="text-sm text-brand-text-secondary">
                                <p>Percentage: <span className="font-semibold">{scorePercentage}%</span></p>
                                <p>Attempted: <span className="font-semibold">{attemptedQuestions}/{totalQuestions}</span></p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Performance Chart */}
                    <div className="lg:col-span-1">
                        <PerformanceChart results={results} />
                    </div>
                    
                    {/* Stats Grid */}
                    <div className="lg:col-span-1 space-y-4">
                        <StatCard 
                            label="Correct Answers" 
                            value={results.correct} 
                            subtitle={`+${results.correct * 4} marks`}
                            icon={<CheckCircleIcon className="w-6 h-6" />} 
                            colorClass="text-green-500" 
                        />
                        <StatCard 
                            label="Incorrect Answers" 
                            value={results.incorrect} 
                            subtitle={`-${results.incorrect * (test.negativeMarks || 1)} marks`}
                            icon={<XIcon className="w-6 h-6" />} 
                            colorClass="text-red-500" 
                        />
                        <StatCard 
                            label="Time Taken" 
                            value={timeTaken} 
                            subtitle={`${test.duration} min available`}
                            icon={<ClockIcon className="w-6 h-6" />} 
                            colorClass="text-blue-500" 
                        />
                    </div>
                </div>

                {/* Quick Insights */}
                <div className="bg-brand-surface border border-brand-border rounded-xl p-6 mb-8">
                    <h3 className="text-lg font-semibold mb-4 text-brand-text-primary">Quick Insights</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="text-center p-4 bg-brand-bg rounded-lg">
                            <div className="text-2xl font-bold text-brand-primary">{results.accuracy}%</div>
                            <div className="text-brand-text-secondary">Overall Accuracy</div>
                        </div>
                        <div className="text-center p-4 bg-brand-bg rounded-lg">
                            <div className="text-2xl font-bold text-brand-primary">{Math.round((attemptedQuestions / totalQuestions) * 100)}%</div>
                            <div className="text-brand-text-secondary">Attempt Rate</div>
                        </div>
                        <div className="text-center p-4 bg-brand-bg rounded-lg">
                            <div className="text-2xl font-bold text-brand-primary">{Math.round((results.time_taken / (test.duration * 60)) * 100)}%</div>
                            <div className="text-brand-text-secondary">Time Utilized</div>
                        </div>
                    </div>
                </div>

                {/* Performance Analytics / Lab */}
                <div id="performance-lab" className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-bold text-brand-text-primary">Performance Lab</h2>
                        <button 
                            onClick={() => setShowAnalytics(!showAnalytics)}
                            className="text-sm font-semibold text-brand-primary hover:underline"
                        >
                            {showAnalytics ? 'Collapse' : 'Expand'} Detailed Insights
                        </button>
                    </div>
                    {showAnalytics && (
                        <PerformanceAnalytics results={results} test={test} questions={test.questions || test.Questions || []} />
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                    <button 
                        onClick={onBack} 
                        className="w-full sm:w-auto px-6 py-3 text-base font-semibold rounded-lg border border-brand-border hover:bg-brand-surface transition-colors"
                    >
                        Back to Dashboard
                    </button>
                    <button 
                        onClick={() => {
                            const element = document.getElementById('performance-lab');
                            element?.scrollIntoView({ behavior: 'smooth' });
                            setShowAnalytics(true);
                        }}
                        className="w-full sm:w-auto px-6 py-3 text-base font-semibold rounded-lg border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-colors"
                    >
                        View Performance Insights
                    </button>
                    {user && (
                        <button 
                            onClick={() => setShowCertificate(true)}
                            className="w-full sm:w-auto px-6 py-3 text-base font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                        >
                            Get Certificate
                        </button>
                    )}
                    <button 
                        onClick={() => {
                            // Store test data and results in localStorage before navigating
                            const testDataToStore = {
                                ...test,
                                Questions: test.questions || test.Questions || [],
                                questions: test.questions || test.Questions || [], // Store both for compatibility
                                id: test.id,
                                name: test.name || test.Name,
                                totalMarks: test.totalMarks || (test.questions?.length || test.Questions?.length || 0) * 4
                            };
                            
                            console.log('Storing test data:', testDataToStore);
                            console.log('Storing results:', results);
                            
                            localStorage.setItem(`testResults_${test.id}`, JSON.stringify(results));
                            localStorage.setItem(`testData_${test.id}`, JSON.stringify(testDataToStore));
                            onViewDetails();
                        }} 
                        className="w-full sm:w-auto px-6 py-3 text-base font-semibold text-white bg-brand-primary rounded-lg hover:bg-brand-primary-hover transition-colors shadow-sm"
                    >
                        View Detailed Analysis
                    </button>
                </div>
                
                {/* Certificate Modal */}
                {showCertificate && user && (
                    <TestCertificate
                        user={user}
                        test={test}
                        results={results}
                        onClose={() => setShowCertificate(false)}
                    />
                )}
            </div>
        </div>
    );
};
